import { useState, useEffect } from 'react';
import * as React from 'react';
import {
  FaPlus, FaTimes, FaSave, FaEdit, FaToggleOn, FaToggleOff,
  FaChevronDown, FaChevronUp, FaGripVertical, FaCopy, FaTrash,
  FaAlignLeft, FaListUl, FaCheckSquare, FaCaretSquareDown,
  FaStar, FaSlidersH, FaCalendarAlt, FaClock, FaHashtag,
  FaFont, FaParagraph,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { colors, shadows } from '../../employees/styles/personalakteStyles';
import InterviewService from '../services/InterviewService';
import type { InterviewCriteriaTemplate, InterviewCriterion, InterviewCustomQuestion, TeamChoice, DesiredEmploymentType } from '../types';

const QUESTION_TYPES: { value: InterviewCustomQuestion['type']; label: string; icon: React.ReactNode }[] = [
  { value: 'text', label: 'Kurzantwort', icon: <FaFont size={14} /> },
  { value: 'textarea', label: 'Absatz', icon: <FaParagraph size={14} /> },
  { value: 'radio', label: 'Multiple-Choice', icon: <FaListUl size={14} /> },
  { value: 'checkbox', label: 'Kästchen', icon: <FaCheckSquare size={14} /> },
  { value: 'select', label: 'Drop-down', icon: <FaCaretSquareDown size={14} /> },
  { value: 'linear_scale', label: 'Lineare Skala', icon: <FaSlidersH size={14} /> },
  { value: 'rating', label: 'Bewertung', icon: <FaStar size={14} /> },
  { value: 'yes_no', label: 'Ja / Nein', icon: <FaToggleOn size={14} /> },
  { value: 'number', label: 'Zahl', icon: <FaHashtag size={14} /> },
  { value: 'date', label: 'Datum', icon: <FaCalendarAlt size={14} /> },
  { value: 'date_range', label: 'Zeitraum', icon: <FaCalendarAlt size={14} /> },
  { value: 'time', label: 'Zeit', icon: <FaClock size={14} /> },
];

const TYPE_LABELS: Record<string, string> = Object.fromEntries(QUESTION_TYPES.map(t => [t.value, t.label]));

export default function InterviewTemplateEditor(): React.JSX.Element {
  const [templates, setTemplates] = useState<InterviewCriteriaTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<number | 'new' | null>(null);

  useEffect(() => { loadTemplates(); }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const data = await InterviewService.fetchTemplates();
      setTemplates(data);
    } catch (e: any) {
      toast.error('Interview-Templates konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 40, color: colors.textMuted, fontSize: 13 }}>Lade Templates...</div>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: colors.textPrimary }}>
          Interview-Templates ({templates.length})
        </h3>
        <button
          onClick={() => setEditingId('new')}
          style={{
            display: 'flex', alignItems: 'center', gap: 5,
            padding: '7px 14px', borderRadius: 8, border: 'none',
            background: 'linear-gradient(135deg, #5271A5, #3b5998)', color: 'white',
            cursor: 'pointer', fontSize: 12, fontWeight: 600,
          }}
        >
          <FaPlus style={{ fontSize: 10 }} /> Neues Template
        </button>
      </div>

      {editingId === 'new' && (
        <TemplateFormBuilder
          onSave={async (data) => {
            await InterviewService.createTemplate(data);
            toast.success('Template erstellt.');
            setEditingId(null);
            loadTemplates();
          }}
          onCancel={() => setEditingId(null)}
        />
      )}

      {templates.map(template => (
        <div key={template.id}>
          {editingId === template.id ? (
            <TemplateFormBuilder
              initial={template}
              onSave={async (data) => {
                await InterviewService.updateTemplate(template.id, data);
                toast.success('Template aktualisiert.');
                setEditingId(null);
                loadTemplates();
              }}
              onCancel={() => setEditingId(null)}
            />
          ) : (
            <TemplateCard template={template} onEdit={() => setEditingId(template.id)} />
          )}
        </div>
      ))}

      {templates.length === 0 && editingId !== 'new' && (
        <div style={{
          textAlign: 'center', padding: 40, color: colors.textMuted, fontSize: 13,
          background: colors.bgCard, borderRadius: 16, border: `1px solid ${colors.border}`,
        }}>
          Noch keine Interview-Templates vorhanden. Erstelle ein Template um den Interviewprozess zu strukturieren.
        </div>
      )}
    </div>
  );
}

/* ---- Template Card (read-only) ---- */

function TemplateCard({ template, onEdit }: { template: InterviewCriteriaTemplate; onEdit: () => void }): React.JSX.Element {
  const [expanded, setExpanded] = useState(false);

  return (
    <div style={{
      background: colors.bgCard, borderRadius: 16, border: `1px solid ${colors.border}`,
      boxShadow: shadows.card, padding: '16px 20px',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button
            onClick={() => setExpanded(!expanded)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: colors.textMuted, fontSize: 12, padding: 0 }}
          >
            {expanded ? <FaChevronUp /> : <FaChevronDown />}
          </button>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: colors.textPrimary }}>{template.name}</div>
            <div style={{ fontSize: 11, color: colors.textMuted }}>
              {template.team ? (template.team === 'SERVICE' ? 'Service' : 'Produktion') : 'Alle Teams'}
              {' | '}
              {template.employment_type || 'Alle Typen'}
              {' | '}
              {template.criteria.length} Kriterien, {template.custom_questions.length} Fragen
            </div>
          </div>
          <span style={{
            padding: '2px 8px', borderRadius: 9999, fontSize: 10, fontWeight: 600,
            background: template.is_active ? colors.successLight : colors.bgMuted,
            color: template.is_active ? '#166534' : colors.textMuted,
          }}>
            {template.is_active ? 'Aktiv' : 'Inaktiv'}
          </span>
        </div>
        <button
          onClick={onEdit}
          style={{
            display: 'flex', alignItems: 'center', gap: 4,
            padding: '5px 10px', borderRadius: 6, border: `1px solid ${colors.border}`,
            background: colors.bgCard, cursor: 'pointer', fontSize: 11, color: colors.textBody,
          }}
        >
          <FaEdit style={{ fontSize: 10 }} /> Bearbeiten
        </button>
      </div>

      {expanded && (
        <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${colors.borderLight}` }}>
          {template.custom_questions.length > 0 && template.custom_questions.map((q, qi) => (
            <div key={q.key} style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
              background: colors.bgMuted, borderRadius: 10, marginBottom: 6,
              border: `1px solid ${colors.borderLight}`,
            }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: colors.textMuted, width: 20, textAlign: 'right' }}>{qi + 1}.</span>
              <span style={{ flex: 1, fontSize: 12, color: colors.textBody }}>{q.label}</span>
              <span style={{
                fontSize: 9, fontWeight: 600, color: colors.textMuted,
                padding: '2px 6px', background: colors.bgCard, borderRadius: 4,
                border: `1px solid ${colors.borderLight}`,
              }}>
                {TYPE_LABELS[q.type] || q.type}
              </span>
              {q.required && <span style={{ fontSize: 9, fontWeight: 700, color: colors.danger }}>Pflicht</span>}
            </div>
          ))}
          {template.custom_questions.length === 0 && (
            <div style={{ fontSize: 12, color: colors.textMuted, fontStyle: 'italic' }}>Keine Fragen definiert.</div>
          )}
        </div>
      )}
    </div>
  );
}

/* ---- Google-Forms-Style Builder ---- */

interface TemplateFormBuilderProps {
  initial?: InterviewCriteriaTemplate;
  onSave: (data: Partial<InterviewCriteriaTemplate>) => Promise<void>;
  onCancel: () => void;
}

function TemplateFormBuilder({ initial, onSave, onCancel }: TemplateFormBuilderProps): React.JSX.Element {
  const [name, setName] = useState(initial?.name || '');
  const [team, setTeam] = useState<TeamChoice | ''>(initial?.team || '');
  const [employmentType, setEmploymentType] = useState<DesiredEmploymentType | ''>(initial?.employment_type || '');
  const [isActive, setIsActive] = useState(initial?.is_active ?? true);
  const [questions, setQuestions] = useState<InterviewCustomQuestion[]>(initial?.custom_questions || []);
  const [criteria, setCriteria] = useState<InterviewCriterion[]>(initial?.criteria || []);
  const [selectedIdx, setSelectedIdx] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [showTypeMenu, setShowTypeMenu] = useState(false);

  const handleSubmit = async () => {
    if (!name.trim()) { toast.error('Template-Name ist erforderlich.'); return; }
    try {
      setSaving(true);
      await onSave({
        name, team: team || undefined, employment_type: employmentType || undefined,
        is_active: isActive, criteria, custom_questions: questions,
      });
    } catch { toast.error('Speichern fehlgeschlagen.'); } finally { setSaving(false); }
  };

  const addQuestion = (type: InterviewCustomQuestion['type']) => {
    const typeInfo = QUESTION_TYPES.find(t => t.value === type);
    const newQ: InterviewCustomQuestion = {
      key: `q_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      label: '',
      type,
      required: false,
      ...(type === 'radio' || type === 'checkbox' || type === 'select' ? { options: ['Option 1'] } : {}),
      ...(type === 'linear_scale' ? { scale_min: 1, scale_max: 5, scale_min_label: '', scale_max_label: '' } : {}),
    };
    setQuestions(prev => [...prev, newQ]);
    setSelectedIdx(questions.length);
    setShowTypeMenu(false);
  };

  const updateQuestion = (idx: number, updates: Partial<InterviewCustomQuestion>) => {
    setQuestions(prev => prev.map((q, i) => i === idx ? { ...q, ...updates } : q));
  };

  const removeQuestion = (idx: number) => {
    setQuestions(prev => prev.filter((_, i) => i !== idx));
    setSelectedIdx(null);
  };

  const duplicateQuestion = (idx: number) => {
    const q = questions[idx];
    const dup = { ...q, key: `q_${Date.now()}_${Math.random().toString(36).slice(2, 6)}` };
    setQuestions(prev => [...prev.slice(0, idx + 1), dup, ...prev.slice(idx + 1)]);
    setSelectedIdx(idx + 1);
  };

  const moveQuestion = (idx: number, dir: -1 | 1) => {
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= questions.length) return;
    setQuestions(prev => {
      const arr = [...prev];
      [arr[idx], arr[newIdx]] = [arr[newIdx], arr[idx]];
      return arr;
    });
    setSelectedIdx(newIdx);
  };

  const inputStyle: React.CSSProperties = {
    padding: '8px 0', border: 'none', borderBottom: `1px solid ${colors.border}`,
    fontSize: 14, outline: 'none', width: '100%', background: 'transparent',
    color: colors.textBody,
  };

  return (
    <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
      {/* Main content */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 12 }}>

        {/* Header card */}
        <div style={{
          background: colors.bgCard, borderRadius: 12, padding: '20px 24px',
          border: `1px solid ${colors.border}`, borderTop: `3px solid ${colors.slate}`,
        }}>
          <input
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="Unbenanntes Template"
            style={{ ...inputStyle, fontSize: 22, fontWeight: 500, borderBottom: `2px solid ${colors.slate}`, marginBottom: 12 }}
          />
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <select value={team} onChange={e => setTeam(e.target.value as any)} style={{
              padding: '6px 10px', borderRadius: 8, border: `1px solid ${colors.border}`,
              fontSize: 12, color: colors.textBody, background: colors.bgCard, outline: 'none',
            }}>
              <option value="">Alle Teams</option>
              <option value="SERVICE">Service</option>
              <option value="PRODUCTION">Produktion</option>
            </select>
            <select value={employmentType} onChange={e => setEmploymentType(e.target.value as any)} style={{
              padding: '6px 10px', borderRadius: 8, border: `1px solid ${colors.border}`,
              fontSize: 12, color: colors.textBody, background: colors.bgCard, outline: 'none',
            }}>
              <option value="">Alle Typen</option>
              <option value="MINIJOB">Minijob</option>
              <option value="WERKSTUDENT">Werkstudent</option>
              <option value="TEILZEIT">Teilzeit</option>
              <option value="VOLLZEIT">Vollzeit</option>
            </select>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 12, color: colors.textBody }}>Aktiv</span>
              <button onClick={() => setIsActive(!isActive)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, padding: 0 }}>
                {isActive ? <FaToggleOn color={colors.success} /> : <FaToggleOff color={colors.textMuted} />}
              </button>
            </div>
          </div>
        </div>

        {/* Question cards */}
        {questions.map((q, idx) => {
          const isSelected = selectedIdx === idx;
          return (
            <QuestionCard
              key={q.key}
              question={q}
              index={idx}
              total={questions.length}
              isSelected={isSelected}
              onSelect={() => setSelectedIdx(idx)}
              onChange={(updates) => updateQuestion(idx, updates)}
              onRemove={() => removeQuestion(idx)}
              onDuplicate={() => duplicateQuestion(idx)}
              onMove={(dir) => moveQuestion(idx, dir)}
            />
          );
        })}

        {/* Empty state */}
        {questions.length === 0 && (
          <div
            onClick={() => addQuestion('radio')}
            style={{
              background: colors.bgCard, borderRadius: 12, padding: '40px 24px',
              border: `2px dashed ${colors.border}`, textAlign: 'center',
              cursor: 'pointer', transition: 'all 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = colors.slate; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = colors.border; }}
          >
            <FaPlus size={20} color={colors.textMuted} />
            <div style={{ fontSize: 13, color: colors.textMuted, marginTop: 8 }}>Erste Frage hinzufügen</div>
          </div>
        )}

        {/* Save / Cancel bar */}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', paddingTop: 8 }}>
          <button onClick={onCancel} style={{
            padding: '8px 16px', borderRadius: 8, border: `1px solid ${colors.border}`,
            background: colors.bgCard, cursor: 'pointer', fontSize: 12, fontWeight: 500,
          }}>
            Abbrechen
          </button>
          <button onClick={handleSubmit} disabled={saving} style={{
            display: 'flex', alignItems: 'center', gap: 5,
            padding: '8px 16px', borderRadius: 8, border: 'none',
            background: `linear-gradient(90deg, ${colors.navy} 0%, ${colors.navy} 30%, ${colors.magenta} 100%)`,
            color: 'white', cursor: saving ? 'not-allowed' : 'pointer',
            fontSize: 12, fontWeight: 600, opacity: saving ? 0.7 : 1,
          }}>
            <FaSave style={{ fontSize: 11 }} /> {saving ? 'Speichern...' : 'Speichern'}
          </button>
        </div>
      </div>

      {/* Floating toolbar (right side) */}
      <div style={{
        position: 'sticky', top: 80,
        display: 'flex', flexDirection: 'column', gap: 2,
        background: colors.bgCard, borderRadius: 10,
        border: `1px solid ${colors.border}`, boxShadow: shadows.card,
        padding: '6px', width: 44,
      }}>
        {/* Add question */}
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => setShowTypeMenu(!showTypeMenu)}
            title="Frage hinzufügen"
            style={{
              width: 32, height: 32, borderRadius: 8,
              background: 'transparent', border: 'none',
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: colors.textMuted, transition: 'all 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = colors.bgMuted; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
          >
            <FaPlus size={14} />
          </button>

          {/* Type dropdown */}
          {showTypeMenu && (
            <>
              <div style={{ position: 'fixed', inset: 0, zIndex: 998 }} onClick={() => setShowTypeMenu(false)} />
              <div style={{
                position: 'absolute', right: 44, top: 0,
                background: colors.bgCard, borderRadius: 10, padding: '8px 0',
                border: `1px solid ${colors.border}`, boxShadow: shadows.elevated,
                zIndex: 999, minWidth: 200,
              }}>
                {QUESTION_TYPES.map(t => (
                  <button
                    key={t.value}
                    onClick={() => addQuestion(t.value)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      width: '100%', padding: '8px 16px',
                      background: 'transparent', border: 'none', cursor: 'pointer',
                      fontSize: 13, color: colors.textBody, textAlign: 'left',
                      transition: 'background 0.1s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = colors.bgMuted; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    <span style={{ color: colors.textMuted, width: 20, display: 'flex', justifyContent: 'center' }}>{t.icon}</span>
                    {t.label}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Quick add shortcuts */}
        <div style={{ height: 1, background: colors.borderLight, margin: '2px 4px' }} />
        {[
          { type: 'text' as const, icon: <FaFont size={12} />, title: 'Kurzantwort' },
          { type: 'radio' as const, icon: <FaListUl size={12} />, title: 'Multiple-Choice' },
          { type: 'checkbox' as const, icon: <FaCheckSquare size={12} />, title: 'Kästchen' },
          { type: 'date' as const, icon: <FaCalendarAlt size={12} />, title: 'Datum' },
        ].map(s => (
          <button
            key={s.type}
            onClick={() => addQuestion(s.type)}
            title={s.title}
            style={{
              width: 32, height: 32, borderRadius: 8,
              background: 'transparent', border: 'none',
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: colors.textMuted, transition: 'all 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = colors.bgMuted; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
          >
            {s.icon}
          </button>
        ))}
      </div>
    </div>
  );
}

/* ---- Individual Question Card ---- */

interface QuestionCardProps {
  question: InterviewCustomQuestion;
  index: number;
  total: number;
  isSelected: boolean;
  onSelect: () => void;
  onChange: (updates: Partial<InterviewCustomQuestion>) => void;
  onRemove: () => void;
  onDuplicate: () => void;
  onMove: (dir: -1 | 1) => void;
}

function QuestionCard({ question: q, index, total, isSelected, onSelect, onChange, onRemove, onDuplicate, onMove }: QuestionCardProps): React.JSX.Element {
  const needsOptions = q.type === 'radio' || q.type === 'checkbox' || q.type === 'select';

  const addOption = () => {
    const opts = [...(q.options || []), `Option ${(q.options?.length || 0) + 1}`];
    onChange({ options: opts });
  };

  const updateOption = (oi: number, val: string) => {
    const opts = [...(q.options || [])];
    opts[oi] = val;
    onChange({ options: opts });
  };

  const removeOption = (oi: number) => {
    onChange({ options: (q.options || []).filter((_, j) => j !== oi) });
  };

  return (
    <div
      onClick={onSelect}
      style={{
        background: colors.bgCard,
        borderRadius: 12,
        padding: '16px 20px',
        border: `1px solid ${isSelected ? colors.slate : colors.border}`,
        borderLeft: isSelected ? `4px solid ${colors.slate}` : `1px solid ${colors.border}`,
        transition: 'border 0.15s, box-shadow 0.15s',
        boxShadow: isSelected ? `0 2px 8px rgba(82, 113, 165, 0.12)` : shadows.card,
        cursor: 'default',
        position: 'relative',
      }}
    >
      {/* Drag handle */}
      {isSelected && (
        <div style={{
          position: 'absolute', top: 4, left: '50%', transform: 'translateX(-50%)',
          color: colors.borderLight, cursor: 'grab',
        }}>
          <FaGripVertical size={10} style={{ transform: 'rotate(90deg)' }} />
        </div>
      )}

      {/* Question header: label + type selector */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 12 }}>
        <div style={{ flex: 1 }}>
          {isSelected ? (
            <input
              autoFocus
              value={q.label}
              onChange={e => onChange({ label: e.target.value })}
              placeholder="Frage eingeben"
              style={{
                width: '100%', padding: '8px 0', border: 'none',
                borderBottom: `2px solid ${colors.slate}`,
                fontSize: 15, outline: 'none', background: 'transparent',
                color: colors.textPrimary, fontWeight: 500,
              }}
            />
          ) : (
            <div style={{ fontSize: 14, color: q.label ? colors.textPrimary : colors.textMuted, fontWeight: 500, padding: '8px 0' }}>
              {q.label || 'Unbenannte Frage'}
              {q.required && <span style={{ color: colors.danger, marginLeft: 2 }}>*</span>}
            </div>
          )}
        </div>

        {isSelected && (
          <select
            value={q.type}
            onChange={e => {
              const newType = e.target.value as InterviewCustomQuestion['type'];
              const updates: Partial<InterviewCustomQuestion> = { type: newType };
              if ((newType === 'radio' || newType === 'checkbox' || newType === 'select') && (!q.options || q.options.length === 0)) {
                updates.options = ['Option 1'];
              }
              if (newType === 'linear_scale') {
                updates.scale_min = q.scale_min ?? 1;
                updates.scale_max = q.scale_max ?? 5;
              }
              onChange(updates);
            }}
            style={{
              padding: '6px 10px', borderRadius: 8, border: `1px solid ${colors.border}`,
              fontSize: 12, color: colors.textBody, background: colors.bgCard, outline: 'none',
              flexShrink: 0,
            }}
          >
            {QUESTION_TYPES.map(t => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        )}
      </div>

      {/* Answer preview / editor */}
      <div style={{ marginBottom: isSelected ? 12 : 0 }}>
        {/* Text types */}
        {q.type === 'text' && (
          <div style={{ borderBottom: `1px dotted ${colors.border}`, padding: '6px 0', fontSize: 13, color: colors.textMuted, width: '60%' }}>
            Kurzantwort-Text
          </div>
        )}
        {q.type === 'textarea' && (
          <div style={{ borderBottom: `1px dotted ${colors.border}`, padding: '6px 0', fontSize: 13, color: colors.textMuted, width: '90%' }}>
            Langtext-Antwort
          </div>
        )}

        {/* Radio / Checkbox / Select options */}
        {needsOptions && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {(q.options || []).map((opt, oi) => (
              <div key={oi} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {/* Radio/Checkbox indicator */}
                <span style={{
                  width: 18, height: 18, borderRadius: q.type === 'checkbox' ? 3 : '50%',
                  border: `2px solid ${colors.border}`, flexShrink: 0,
                  display: q.type === 'select' ? 'none' : 'block',
                }} />
                {q.type === 'select' && (
                  <span style={{ fontSize: 12, color: colors.textMuted, width: 18, textAlign: 'center' }}>{oi + 1}.</span>
                )}
                {isSelected ? (
                  <>
                    <input
                      value={opt}
                      onChange={e => updateOption(oi, e.target.value)}
                      style={{
                        flex: 1, padding: '4px 0', border: 'none',
                        borderBottom: `1px solid ${colors.borderLight}`,
                        fontSize: 13, outline: 'none', background: 'transparent',
                        color: colors.textBody,
                      }}
                    />
                    <button onClick={() => removeOption(oi)} style={{
                      background: 'none', border: 'none', cursor: 'pointer',
                      color: colors.textMuted, padding: 2,
                    }}>
                      <FaTimes size={10} />
                    </button>
                  </>
                ) : (
                  <span style={{ fontSize: 13, color: colors.textBody }}>{opt}</span>
                )}
              </div>
            ))}
            {isSelected && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{
                  width: 18, height: 18, borderRadius: q.type === 'checkbox' ? 3 : '50%',
                  border: `2px solid ${colors.borderLight}`, flexShrink: 0,
                  display: q.type === 'select' ? 'none' : 'block',
                }} />
                <button
                  onClick={addOption}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    fontSize: 13, color: colors.slate, padding: '4px 0',
                  }}
                >
                  Option hinzufügen
                </button>
              </div>
            )}
          </div>
        )}

        {/* Linear Scale */}
        {q.type === 'linear_scale' && (
          <div>
            {isSelected ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                  <select value={q.scale_min ?? 1} onChange={e => onChange({ scale_min: Number(e.target.value) })} style={{
                    padding: '4px 8px', borderRadius: 6, border: `1px solid ${colors.border}`, fontSize: 12,
                  }}>
                    <option value={0}>0</option>
                    <option value={1}>1</option>
                  </select>
                  <span style={{ color: colors.textMuted }}>bis</span>
                  <select value={q.scale_max ?? 5} onChange={e => onChange({ scale_max: Number(e.target.value) })} style={{
                    padding: '4px 8px', borderRadius: 6, border: `1px solid ${colors.border}`, fontSize: 12,
                  }}>
                    {[2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input value={q.scale_min_label || ''} onChange={e => onChange({ scale_min_label: e.target.value })}
                    placeholder="Label (z.B. Schlecht)" style={{ flex: 1, padding: '4px 8px', borderRadius: 6, border: `1px solid ${colors.border}`, fontSize: 12, outline: 'none' }} />
                  <input value={q.scale_max_label || ''} onChange={e => onChange({ scale_max_label: e.target.value })}
                    placeholder="Label (z.B. Sehr gut)" style={{ flex: 1, padding: '4px 8px', borderRadius: 6, border: `1px solid ${colors.border}`, fontSize: 12, outline: 'none' }} />
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {q.scale_min_label && <span style={{ fontSize: 11, color: colors.textMuted }}>{q.scale_min_label}</span>}
                {Array.from({ length: (q.scale_max ?? 5) - (q.scale_min ?? 1) + 1 }, (_, i) => (q.scale_min ?? 1) + i).map(n => (
                  <span key={n} style={{
                    width: 28, height: 28, borderRadius: '50%',
                    border: `1px solid ${colors.border}`, background: colors.bgCard,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 11, color: colors.textMuted,
                  }}>{n}</span>
                ))}
                {q.scale_max_label && <span style={{ fontSize: 11, color: colors.textMuted }}>{q.scale_max_label}</span>}
              </div>
            )}
          </div>
        )}

        {/* Rating */}
        {q.type === 'rating' && (
          <div style={{ display: 'flex', gap: 4 }}>
            {[1, 2, 3, 4, 5].map(n => (
              <FaStar key={n} size={20} color={colors.borderLight} />
            ))}
          </div>
        )}

        {/* Yes/No */}
        {q.type === 'yes_no' && (
          <div style={{ display: 'flex', gap: 8 }}>
            {['Ja', 'Nein'].map(opt => (
              <span key={opt} style={{
                padding: '6px 20px', borderRadius: 9999,
                border: `1px solid ${colors.border}`, background: colors.bgMuted,
                fontSize: 13, color: colors.textBody,
              }}>{opt}</span>
            ))}
          </div>
        )}

        {/* Number */}
        {q.type === 'number' && (
          <div style={{ borderBottom: `1px dotted ${colors.border}`, padding: '6px 0', fontSize: 13, color: colors.textMuted, width: '30%' }}>
            Zahl
          </div>
        )}

        {/* Date */}
        {q.type === 'date' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ borderBottom: `1px dotted ${colors.border}`, padding: '6px 0', fontSize: 13, color: colors.textMuted, width: 150 }}>
              Tag. Monat. Jahr
            </div>
            <FaCalendarAlt size={14} color={colors.textMuted} />
          </div>
        )}

        {/* Date Range */}
        {q.type === 'date_range' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ borderBottom: `1px dotted ${colors.border}`, padding: '6px 0', fontSize: 13, color: colors.textMuted, width: 130 }}>
              Von
            </div>
            <span style={{ color: colors.textMuted }}>–</span>
            <div style={{ borderBottom: `1px dotted ${colors.border}`, padding: '6px 0', fontSize: 13, color: colors.textMuted, width: 130 }}>
              Bis
            </div>
            <FaCalendarAlt size={14} color={colors.textMuted} />
          </div>
        )}

        {/* Time */}
        {q.type === 'time' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ borderBottom: `1px dotted ${colors.border}`, padding: '6px 0', fontSize: 13, color: colors.textMuted, width: 100 }}>
              Stunde : Minute
            </div>
            <FaClock size={14} color={colors.textMuted} />
          </div>
        )}
      </div>

      {/* Bottom toolbar (visible when selected) */}
      {isSelected && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
          gap: 8, paddingTop: 12, borderTop: `1px solid ${colors.borderLight}`,
        }}>
          {/* Move */}
          <button onClick={(e) => { e.stopPropagation(); onMove(-1); }} disabled={index === 0}
            style={{ background: 'none', border: 'none', cursor: index === 0 ? 'default' : 'pointer', color: index === 0 ? colors.borderLight : colors.textMuted, padding: 4 }}>
            <FaChevronUp size={12} />
          </button>
          <button onClick={(e) => { e.stopPropagation(); onMove(1); }} disabled={index === total - 1}
            style={{ background: 'none', border: 'none', cursor: index === total - 1 ? 'default' : 'pointer', color: index === total - 1 ? colors.borderLight : colors.textMuted, padding: 4 }}>
            <FaChevronDown size={12} />
          </button>

          <div style={{ width: 1, height: 20, background: colors.borderLight }} />

          {/* Duplicate */}
          <button onClick={(e) => { e.stopPropagation(); onDuplicate(); }} title="Duplizieren"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: colors.textMuted, padding: 4 }}>
            <FaCopy size={12} />
          </button>

          {/* Delete */}
          <button onClick={(e) => { e.stopPropagation(); onRemove(); }} title="Löschen"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: colors.textMuted, padding: 4 }}>
            <FaTrash size={12} />
          </button>

          <div style={{ width: 1, height: 20, background: colors.borderLight }} />

          {/* Required toggle */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: colors.textBody, cursor: 'pointer' }}
            onClick={(e) => { e.stopPropagation(); onChange({ required: !q.required }); }}>
            Pflicht
            <span style={{ fontSize: 20, display: 'flex' }}>
              {q.required ? <FaToggleOn color={colors.slate} /> : <FaToggleOff color={colors.textMuted} />}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
